import {
  collection, doc, addDoc, updateDoc, deleteDoc,
  query, orderBy, getDocs, getDoc, where, serverTimestamp, limit,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from './firebase';

const COL = 'blogPosts';

/* ── Helpers ── */
function slugify(text) {
  return text
    .toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

function estimateReadTime(markdown) {
  const words = (markdown || '').split(/\s+/).filter(Boolean).length;
  const minutes = Math.max(1, Math.ceil(words / 200));
  return `${minutes} min`;
}

function docToPost(snap) {
  const d = snap.data();
  return {
    id: snap.id,
    slug: d.slug || '',
    title: d.title || '',
    subtitle: d.subtitle || '',
    category: d.category || '',
    categoryCor: d.categoryCor || '#3B82F6',
    content: d.content || '',           // markdown
    tags: d.tags || [],
    author: d.author || { name: '', role: '', ini: '', cor: '#3B82F6' },
    coverGradient: d.coverGradient || 'linear-gradient(135deg,#1e3a8a,#0A0F1E)',
    coverEmoji: d.coverEmoji || '📝',
    coverImageUrl: d.coverImageUrl || null,
    published: d.published ?? false,
    readTime: d.readTime || estimateReadTime(d.content),
    date: d.date || '',
    criadoEm: d.criadoEm,
    atualizadoEm: d.atualizadoEm,
    metaTitle: d.metaTitle || '',
    metaDescription: d.metaDescription || '',
  };
}

/* ── CREATE ── */
export async function createPost(data) {
  const slug = data.slug || slugify(data.title);
  const readTime = estimateReadTime(data.content);
  const now = new Date();
  const date = data.date || now.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });

  const docRef = await addDoc(collection(db, COL), {
    ...data,
    slug,
    readTime,
    date,
    published: data.published ?? false,
    criadoEm: serverTimestamp(),
    atualizadoEm: serverTimestamp(),
  });

  return { id: docRef.id, slug };
}

/* ── UPDATE ── */
export async function updatePost(postId, data) {
  const updates = { ...data, atualizadoEm: serverTimestamp() };

  if (data.content) {
    updates.readTime = estimateReadTime(data.content);
  }
  if (data.title && !data.slug) {
    updates.slug = slugify(data.title);
  }

  await updateDoc(doc(db, COL, postId), updates);
}

/* ── DELETE ── */
export async function deletePost(postId) {
  // Remove cover image if exists
  const snap = await getDoc(doc(db, COL, postId));
  if (snap.exists() && snap.data().coverImageUrl) {
    try {
      const imgRef = ref(storage, `blog/${postId}/cover`);
      await deleteObject(imgRef);
    } catch (e) { /* ignore */ }
  }
  await deleteDoc(doc(db, COL, postId));
}

/* ── GET ALL (admin) ── */
export async function getAllPosts() {
  const q = query(collection(db, COL), orderBy('criadoEm', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map(docToPost);
}

/* ── GET PUBLISHED ── */
export async function getPublishedPosts() {
  try {
    // Requires composite index: published ASC + criadoEm DESC
    const q = query(
      collection(db, COL),
      where('published', '==', true),
      orderBy('criadoEm', 'desc')
    );
    const snap = await getDocs(q);
    return snap.docs.map(docToPost);
  } catch (e) {
    // Fallback: if composite index doesn't exist yet, get all and filter client-side
    console.warn('blogService: composite index missing, falling back to client filter', e.message);
    const q = query(collection(db, COL), orderBy('criadoEm', 'desc'));
    const snap = await getDocs(q);
    return snap.docs.map(docToPost).filter(p => p.published);
  }
}

/* ── GET BY SLUG ── */
export async function getPostBySlug(slug) {
  const q = query(collection(db, COL), where('slug', '==', slug), limit(1));
  const snap = await getDocs(q);
  if (snap.empty) return null;
  return docToPost(snap.docs[0]);
}

/* ── GET BY ID ── */
export async function getPostById(postId) {
  const snap = await getDoc(doc(db, COL, postId));
  if (!snap.exists()) return null;
  return docToPost(snap);
}

/* ── UPLOAD COVER IMAGE ── */
export async function uploadCoverImage(postId, file) {
  const imgRef = ref(storage, `blog/${postId}/cover`);
  await uploadBytes(imgRef, file);
  const url = await getDownloadURL(imgRef);
  await updateDoc(doc(db, COL, postId), { coverImageUrl: url });
  return url;
}

/* ── TOGGLE PUBLISH ── */
export async function togglePublish(postId, published) {
  await updateDoc(doc(db, COL, postId), {
    published,
    atualizadoEm: serverTimestamp(),
  });
}

/* ── CATEGORIES ── */
export const BLOG_CATEGORIES = [
  { label: 'Crédito Imobiliário', cor: '#3B82F6' },
  { label: 'Ferramentas', cor: '#8B5CF6' },
  { label: 'CET', cor: '#F59E0B' },
  { label: 'MIP/DFI', cor: '#00D68F' },
  { label: 'Mercado', cor: '#F43F5E' },
  { label: 'Dicas', cor: '#14B8A6' },
  { label: 'Tutoriais', cor: '#EC4899' },
];